// features/findings/asymmetric-underdog-inversion.js
// ════════════════════════════════════════════════════════════════════
// LAB FINDING — Asymmetric Underdog Pattern (v1)
// Form 4 (Pattern across cases). First published Lab finding.
// Content sourced verbatim from docs/LAB_FINDING_ASYMMETRIC_UNDERDOG_V1.md
// ════════════════════════════════════════════════════════════════════
// Migrated from pages/findings/strategic/2026/asymmetric-underdog-inversion.js
// in Phase F1b — content unchanged; rendering moved into a finding
// component for the dynamic loader.
// ════════════════════════════════════════════════════════════════════

import {
  FindingBack,
  FindingHeader,
  FindingSection,
  FindingSubhead,
  FindingPill,
  FindingPillRow,
  ChartSlot,
} from './components-shared';
import {
  CoOccurrenceMatrix,
  CausalChainDiagram,
  TornadoPlot,
  PredictionsBlock,
} from './charts';

// ─── Chart data for this finding ─────────────────────────────────────

const COOCCUR_DATA = {
  cases: [
    { id: 'indiana', shortLabel: 'Indiana 2024-25', outcome: 'success' },
    { id: 'walsh', shortLabel: '49ers 1979', outcome: 'success' },
    { id: 'pep', shortLabel: 'Barça 2008', outcome: 'success' },
    { id: 'wells', shortLabel: 'Texas Tech 2018', outcome: 'failure' },
  ],
  signals: [
    {
      id: 'multilevel',
      label: 'Multi-level track record',
      values: {
        indiana: { state: 'present', detail: '3+ levels (D-II → FCS → FBS)' },
        walsh:   { state: 'present', detail: '2 levels (NFL coord + Stanford HC)' },
        pep:     { state: 'partial', detail: '1 HC + lifelong club playing (edge case)' },
        wells:   { state: 'absent',  detail: '1 level only' },
      },
    },
    {
      id: 'voluntary',
      label: 'Voluntary following',
      values: {
        indiana: { state: 'present', detail: '13+ JMU players' },
        walsh:   { state: 'partial', detail: 'WCO coaching network attached' },
        pep:     { state: 'partial', detail: 'internal — locker-room respect' },
        wells:   { state: 'absent',  detail: '~2 staff, no players' },
      },
    },
    {
      id: 'pricing',
      label: 'Market mispricing',
      values: {
        indiana: { state: 'present', detail: 'passed-over for 10+ years' },
        walsh:   { state: 'present', detail: 'NFL had collectively passed' },
        pep:     { state: 'present', detail: 'promoted over Mourinho' },
        wells:   { state: 'absent',  detail: 'market-rate Utah State → Texas Tech' },
      },
    },
  ],
};

const CHAIN_STEPS = [
  { label: 'Multi-level wins → system context-adaptation', confidence: 0.85 },
  { label: 'Voluntary following → locker-room validation', confidence: 0.80 },
  { label: 'Market mispricing → asymmetric edge availability', confidence: 0.75 },
  { label: 'System install → year-1 attack', confidence: 0.85 },
  { label: 'Year-2 doubling-down → championship-window unlock', confidence: 0.90 },
];

const FALSIFIER_DIMS = [
  {
    id: 'multilevel-strict',
    label: 'Strict multi-level coding',
    low: -0.20,
    high: 0.05,
    lowLabel: 'Pep falls into Wells territory; pattern weakens',
    highLabel: 'Pattern unchanged',
  },
  {
    id: 'recency',
    label: 'Recency / era effect',
    low: -0.10,
    high: 0.15,
    lowLabel: 'Cignetti is NIL-era artifact',
    highLabel: 'Pattern transfers across eras',
  },
  {
    id: 'cross-domain',
    label: 'Cross-domain replication',
    low: -0.30,
    high: 0.25,
    lowLabel: 'Sports-specific',
    highLabel: 'Schultz / Wojcicki extend the pattern',
  },
  {
    id: 'survivorship',
    label: 'Survivor bias on failure side',
    low: -0.15,
    high: 0.05,
    lowLabel: 'Many unwritten failures',
    highLabel: 'Texas Tech is representative',
  },
];

export const PREDICTIONS = [
  {
    label: 'Indiana 2026 season (Cignetti year 3): 10+ wins, top-15 finish, no collapse.',
    band: [10, 13],
    central: 11,
    falsifierBand: [6, 14],
    resolutionDate: '2027-01',
    status: 'open',
    note: 'Tests year-3 spotlight resilience. Falsified if record below .500 or Cignetti fired.',
  },
  {
    label: 'Cross-domain — retrospective: Schultz at Starbucks (2008 return) shows all three signals present and tracks success cases.',
    resolutionDate: '2026 retrospective',
    status: 'open',
    note: 'Tests cross-domain mechanism transfer. Validates if multi-level + voluntary + mispricing are present in non-sports contexts.',
  },
  {
    label: 'Anti-prediction — any current asymmetric-underdog hire missing one or more signals fails to produce championship-window outcomes within 5 years.',
    resolutionDate: 'case-specific (rolling)',
    status: 'open',
    note: 'Tests the failure-mode side of the pattern. Requires identifying pre-registered candidates currently in window.',
  },
];

export const FINDING_META = {
  slug: 'asymmetric-underdog-inversion',
  domain: 'strategic',
  year: 2026,
  version: 'v1',
  lifecycle: 'pre-registered', // draft | pre-registered | published | revised | retracted
  prevVersion: null,
  revisedBy: null,
  whatChanged: null,
  retractionNote: null,
  citationHandle: 'lab:finding/strategic/2026/asymmetric-underdog-inversion/v1',
  title: 'The Asymmetric Underdog Pattern',
  shortTitle: 'Asymmetric Underdog Pattern',
  pageTitle: 'Asymmetric Underdog Pattern — Lab Finding · Shift',
  description: 'Three signals that separate real bets from cosplay. A Lab finding across Indiana 2024-25, the 1979 49ers, 2008 Barcelona, and the 2018 Texas Tech failed analog.',
  form: 4,
  formName: 'Pattern across cases',
  reflexivity: 'LOW',
  epistemicStatus: 'mechanism',
  confidence: 'medium',
  sampleSize: 4,
  preregisteredAt: '2026-05-07',
  visibility: 'public-peek', // private | shared-link | public-peek | public-collab
};

export default function AsymmetricUnderdogInversionFinding() {
  return (
    <article className="finding-article">
      <FindingBack />

      <FindingHeader
        eyebrow="Strategic Decisions · Asymmetric Underdog Inversion · 2026-05-07"
        title="The Asymmetric Underdog Pattern"
        sub="Three signals that separate real bets from cosplay."
        subItalic="Across Indiana 2024-25, the 1979 49ers, and 2008 Barcelona — with 2018 Texas Tech as the documented failure."
        meta={FINDING_META}
      />

      {/* ─── Lede ─── */}
      <section className="finding-lede">
        <p>
          When an organization has been mediocre or worse for 5+ years and considers hiring an unconventional candidate — older, from a smaller stage, lacking the conventional pedigree — the conventional read is &ldquo;this is risky.&rdquo; Three documented success cases spanning four decades and three sports tell a different story: the asymmetric bet pays off when three specific signals converge. Missing any one of them and the same unconventional-hire pattern collapses into cosplay. Texas Tech 2018 shows exactly that collapse. The boundary between <em>real asymmetric bet</em> and <em>non-pedigree theater</em> is sharper than conventional wisdom acknowledges, and it&rsquo;s checkable.
        </p>
      </section>

      {/* ─── TL;DR pill card ─── */}
      <section className="finding-tldr">
        <div className="finding-tldr-eyebrow">TL;DR</div>
        <p className="finding-tldr-verdict">
          Asymmetric underdog hires produce championship-window outcomes when three signals converge: <strong>multi-level track record</strong>, <strong>voluntary following from prior stops</strong>, and <strong>market mispricing</strong>. Missing any one and the pattern fails.
        </p>
        <FindingPillRow>
          <FindingPill tone="mechanism">Mechanism documented</FindingPill>
          <FindingPill tone="warn">Confidence: medium</FindingPill>
          <FindingPill tone="neutral">N=4 (3 successes + 1 failure)</FindingPill>
          <FindingPill tone="neutral">Pre-registered 2026-05-07</FindingPill>
          <FindingPill tone="neutral">Reflexivity: LOW</FindingPill>
        </FindingPillRow>
        <div className="finding-tldr-corpus">
          <strong>Corpus:</strong> Indiana 2024-25 (Cignetti) · 1979 49ers (Walsh) · 2008 Barcelona (Pep) · 2018 Texas Tech (Wells, failed analog). 1979–2025, NCAAF + NFL + La Liga.
        </div>
      </section>

      {/* ─── § The pattern ─── */}
      <FindingSection eyebrow="§ The pattern" title="Three signals. Not one. Not two. Three.">
        <p className="finding-prose">
          When all three are present in an unconventional hire, the asymmetric bet pays off — not just in incremental improvement but in <strong>championship-window outcomes</strong> that would have been unreachable under conventional hiring. When even one is missing, the surface pattern (<em>&ldquo;we&rsquo;re hiring the unconventional candidate&rdquo;</em>) becomes cosplay: the failure mode is visible, predictable, and well-documented.
        </p>

        <p className="finding-prose">The three signals:</p>

        <ol className="finding-numbered-list">
          <li>
            <strong>Multi-level track record.</strong> The candidate has won at <em>multiple</em> prior tiers — not one. The system they install has been stress-tested across contexts with different talent constraints, different competitive landscapes, different recruiting realities. <em>Cignetti won at D-II IUP, FCS Elon, and FBS JMU. Walsh ran offense at the NFL level for Cincinnati and head-coached at Stanford. Pep won at Barça B with a championship and played at every level of football for the same club.</em> One-level wins don&rsquo;t generalize.
          </li>
          <li>
            <strong>Voluntary following from prior stops.</strong> People who worked with the candidate at prior positions actively follow them to the new role. Not recruited; not bought; <em>they vote with their feet.</em> Players, assistants, staff who choose to leave their current situation to join. <em>Cignetti pulled 13+ JMU players to Indiana voluntarily.</em> The locker room knows things a hiring committee can&rsquo;t see.
          </li>
          <li>
            <strong>Market mispricing.</strong> The candidate is <em>under-priced</em> by the conventional hiring market. Other peer-tier organizations had access to the same candidate and didn&rsquo;t take the bet — for years. The asymmetric edge only exists in mispriced spaces. <em>Cignetti was passed over for P5 jobs for over a decade despite the multi-level record. Walsh was passed over after Cincinnati; Stanford was the consolation. Pep was promoted from the B team while Mourinho was on the market.</em>
          </li>
        </ol>

        <p className="finding-prose">
          Missing any one and the pattern fails. The 2018 Texas Tech hire of Matt Wells from Utah State <strong>looked like</strong> the same play — non-pedigree, smaller stage, supposedly unconventional — but had only <em>one</em> level of head-coaching wins, almost no voluntary following, and was a market-rate hire rather than a mispriced one. Three years later: 13-18 over three seasons, fired October 2021. The pattern collapsed exactly where the mechanism predicts it should.
        </p>

        <CoOccurrenceMatrix
          title="Co-occurrence matrix — mechanisms × cases"
          cases={COOCCUR_DATA.cases}
          signals={COOCCUR_DATA.signals}
        />
      </FindingSection>

      {/* ─── § The cases ─── */}
      <FindingSection eyebrow="§ The cases" title="Four cases, three signals checked.">
        <CaseCard
          num={1}
          title="Indiana 2024-25 (Curt Cignetti)"
          outcome="success"
          year="hire 2023, championship 2025"
        >
          <p className="finding-prose">
            Indiana football had been mediocre or bad for 130+ years. Nation-leading 713 prior losses at the time of hiring. The conventional read in November 2023 was: hire a rising young P5 coordinator, accept another mediocre 5-7 season, repeat the cycle. Indiana&rsquo;s AD Scott Dolson did something else. He hired Curt Cignetti, a 62-year-old career coach who had won at D-II IUP (going 53-17 in 9 years), built FCS Elon, and just led JMU to an 11-1 record in their <em>first</em> FBS season — but had no P5 head coaching job, ever, in 30+ years of coaching.
          </p>
          <p className="finding-prose">
            What followed is the most extreme version of the pattern the four-case corpus documents. Year 1 (2024 season): 11-1 regular season, first College Football Playoff berth in program history. Year 2 (2025 season): 16-0 undefeated regular season, defeated Miami 27-21 in the CFP final, <strong>first national championship in 130+ years.</strong>
          </p>
          <SignalCheck
            multilevel={{ value: '3+', detail: 'D-II → FCS → FBS, all winning. Three distinct tiers, system intact across each.' }}
            voluntary={{ value: '13+', detail: 'JMU players transferred to Indiana to follow him. The strongest single people-vote signal in the corpus.' }}
            pricing={{ value: 'under-priced', detail: 'P5 programs had passed on Cignetti for over a decade despite the multi-level record.' }}
          />
          <p className="finding-prose">
            The decisive year-2 move that distinguishes this case from a fluke: <strong>Cignetti doubled down rather than coasted.</strong> He landed Mendoza from Cal as a transfer, kept aggressive talent acquisition, refused to let year-1 success make him conservative. Most coaches get cautious after a breakthrough year; Cignetti escalated. The 16-0 + championship is what doubling-down produces.
          </p>
        </CaseCard>

        <CaseCard
          num={2}
          title="1979 San Francisco 49ers (Bill Walsh)"
          outcome="success"
          year="hire 1979, SB XVI 1981 season"
        >
          <p className="finding-prose">
            The 49ers had been the worst team in the NFL in 1978 — 2-14 — and mediocre or bad for the better part of the decade. The hire of Bill Walsh in January 1979 was unconventional in NFL terms: a college head coach (Stanford) with NFL coordinator experience under Paul Brown at Cincinnati, but no NFL head coaching pedigree. The conventional NFL move was an established NFL coordinator from a contender; the 49ers went different.
          </p>
          <p className="finding-prose">
            What unfolded: Year 1 (1979) — <strong>2-14</strong>, no improvement. Year 2 (1980) — 6-10. Year 3 (1981) — <strong>13-3, won Super Bowl XVI.</strong> First championship in franchise history. Decade: three Super Bowls (1981, 1984, 1988), the West Coast Offense becomes the dominant offensive system in the NFL for 30+ years.
          </p>
          <SignalCheck
            multilevel={{ value: '2', detail: 'NFL coordinator under Paul Brown at Cincinnati (where the WCO was developed) + Stanford head coach. Two distinct levels with the system traveling between them.' }}
            voluntary={{ value: 'low (network)', detail: 'Walsh joined a different franchise; the players were existing 49ers, not Stanford imports. His NFL coordinator network — including the WCO coaching tree — voluntarily attached.' }}
            pricing={{ value: 'under-priced', detail: 'Stanford was the consolation prize for a coach the NFL had collectively passed on. The 49ers were available because they were the worst team in the league.' }}
          />
          <p className="finding-prose">
            The Walsh case complicates the <em>year-1 breakthrough</em> narrative. Year 1 was 2-14 — no breakthrough. Year 2 was 6-10. The championship arrived in <em>year 3</em>. This case demonstrates that the pattern doesn&rsquo;t require a year-1 turnaround to be real. What it requires is the three signals plus continued investment through the slow ramp. Walsh drafted Joe Montana late in the 1979 round — the same year his record was 2-14 — because the system told him what to look for. <strong>The system was working before the wins arrived.</strong>
          </p>
        </CaseCard>

        <CaseCard
          num={3}
          title="2008 FC Barcelona (Pep Guardiola)"
          outcome="success"
          year="promoted 2008, treble 2008-09"
        >
          <p className="finding-prose">
            Barça had not won La Liga between 2006 and 2008. Frank Rijkaard&rsquo;s tenure had ended in disarray. The board considered the established candidates: Mourinho was actively on the market. They promoted Pep Guardiola — 37 years old, no senior head coaching experience, fresh off coaching Barça B to a Tercera/Segunda B championship the prior year. The unconventional choice: trust the system Pep had been running at the B level + the playing intelligence he&rsquo;d accumulated as Cruyff&rsquo;s defensive midfielder.
          </p>
          <p className="finding-prose">
            Year 1 (2008-09): <strong>The treble.</strong> La Liga, Copa del Rey, and Champions League in a single season — historically rare in any era of European football. Year 2: La Liga again, plus Club World Cup. Tenure (2008-2012): 14 trophies in 4 years. Tiki-taka becomes the dominant tactical system; the club Cruyff built reaches its modern apex.
          </p>
          <SignalCheck
            multilevel={{ value: '2 (edge case)', detail: 'Only one HC role pre-Barça (the B team), but his playing track record at all levels of football and his B-team championship gave him deep system-context recognition. The pattern\'s edge case.' }}
            voluntary={{ value: 'internal', detail: 'No literal roster brought with him, but the locker room had played for him at B-team level (Busquets, Pedro among them) and trusted the system before he was promoted.' }}
            pricing={{ value: 'under-priced', detail: 'The strongest in the corpus. Pep was promoted OVER Mourinho — arguably the most-coveted manager in Europe at that moment. The board saw value the market hadn\'t priced in.' }}
          />
          <p className="finding-prose">
            The Pep case demonstrates the pattern&rsquo;s robustness across the multi-level signal&rsquo;s weakest version: when the multi-level read is barely there but the system-install and mispricing signals are extreme, the pattern still holds. It also demonstrates the pattern across a different sport entirely — La Liga&rsquo;s competitive structure, financial dynamics, and player development pipelines look nothing like NCAAF or the NFL. <strong>The mechanism transfers anyway.</strong>
          </p>
        </CaseCard>

        <CaseCard
          num={4}
          title="2018 Texas Tech (Matt Wells) — failed analog"
          outcome="failure"
          year="hire 2018, fired 2021"
        >
          <p className="finding-prose">
            Texas Tech had been mediocre under Kliff Kingsbury — 35-40 over six seasons, fired November 2018. The athletic department considered a national search and selected Matt Wells, head coach at Utah State, who had gone 44-34 over six seasons in the Mountain West. Wells was unconventional in P5 terms: came from outside the Power 5, mid-tier program, no high-pedigree credential. The conventional read: Texas Tech was making the same kind of asymmetric bet other programs had been making in non-pedigree directions.
          </p>
          <p className="finding-prose">
            What unfolded: 2019 — 4-8. 2020 — 4-6 (COVID-shortened). 2021 — 5-3 partial season before being fired in October. Cumulative: 13-18 over three years. <strong>Texas Tech&rsquo;s investment produced statistical underperformance against even the modest pre-Wells baseline.</strong>
          </p>
          <SignalCheck
            multilevel={{ value: '1', detail: 'Utah State head coach. No prior head-coaching role at any other tier. The signal was effectively absent.', failed: true }}
            voluntary={{ value: '~2 staff', detail: 'No meaningful player voluntary following. The signal was approximately zero.', failed: true }}
            pricing={{ value: 'market-rate', detail: 'Utah State HC → Texas Tech HC was a reasonable market move at that mid-tier-jumping-to-bottom-P5 price point. The asymmetric edge was absent.', failed: true }}
          />
          <p className="finding-prose">
            All three signals failed simultaneously. The pattern&rsquo;s mechanism predicts what should follow: failure to install system at a higher tier, failure to recruit at the new tier, failure to produce voluntary buy-in from existing players, failure to consolidate identity. All four happened. The 13-18 record over three years is what happens when the unconventional surface is present but the underlying signals are absent.
          </p>
          <p className="finding-prose">
            The Texas Tech case is critical to the pattern&rsquo;s claim. Without it, the three success cases could be cherry-picked. With it, the pattern&rsquo;s failure mode is documented in the inverse: the three signals are not just <em>correlated</em> with success; their absence is <em>correlated</em> with failure in a structurally similar hire. <strong>The mechanism gains considerable explanatory weight from this one failure.</strong>
          </p>
        </CaseCard>
      </FindingSection>

      {/* ─── § The exceptions ─── */}
      <FindingSection eyebrow="§ The exceptions" title="Where the pattern's boundary lives.">
        <p className="finding-prose">
          The pattern doesn&rsquo;t claim to predict every unconventional hire. It claims something narrower: <em>when an organization has been mediocre or worse for 5+ years and considers a candidate that fits the unconventional surface, the three-signal test predicts which hires will produce championship-window outcomes vs which will produce regression to mean.</em>
        </p>

        <p className="finding-prose">The boundary cases that sharpen this:</p>

        <ul className="finding-bullets">
          <li><strong>Lateral moves between similar-tier institutions</strong> — pattern doesn&rsquo;t apply. Coach moves from one P5 to another P5; this isn&rsquo;t an asymmetric underdog hire, it&rsquo;s a market-rate move. The multi-level signal isn&rsquo;t relevant; voluntary following matters less; market mispricing is harder to establish.</li>
          <li><strong>Already-successful organizations hiring unconventional candidates</strong> — different pattern shape. The leverage here is system-fit, not asymmetric mispricing. The &ldquo;drift case&rdquo; covers this: when an org has been good and stalls, the unconventional hire pattern needs re-derivation.</li>
          <li><strong>Talent-rich rosters with the wrong system</strong> — hire is the leverage point only weakly; the system install matters more than who installs it. Pattern applies in attenuated form.</li>
          <li><strong>Domains without the recruiting-pipeline equivalent</strong> — the pattern leans on the candidate&rsquo;s ability to <em>recruit through the system</em>. NCAAF in the NIL/transfer-portal era makes this possible; pre-portal college had different dynamics. NFL needs draft + free-agency capacity. Soccer needs international transfer market access.</li>
          <li><strong>Single-stage candidates with theoretical fit</strong> — the failure-mode territory. This is where Wells lived. Looks unconventional on the surface; theoretically fits the pattern; but the multi-level signal is absent. <strong>The most common way to misuse this pattern: see an unconventional candidate, want to make the asymmetric bet, ignore the missing signals, proceed.</strong> The dossier names this <em>cosplay</em> — and Texas Tech 2018 documents what cosplay produces.</li>
        </ul>

        <ChartSlot
          kind="gap"
          label="Parameter-space coverage heatmap"
          description="Currently shows the corpus is dense in NCAAF + sparse in business/tech adjacents. Renders dynamically once the corpus extends beyond 4 cases."
        />
      </FindingSection>

      {/* ─── § The mechanism ─── */}
      <FindingSection eyebrow="§ The mechanism" title="Why the three-signal pattern holds when it does.">
        <p className="finding-prose">
          The core claim is <em>not</em> that any single signal is causal in isolation. It&rsquo;s that the three signals together constitute evidence of a deeper underlying property: <strong>the candidate has internalized a transferable system that produces wins under varied constraints, and the people who&rsquo;ve worked inside that system know it works.</strong>
        </p>

        <CausalChainDiagram
          title="Causal chain — multi-level wins to championship-window unlock"
          steps={CHAIN_STEPS}
        />

        <p className="finding-prose">The chain:</p>

        <MechanismStep num={1} title="Multi-level wins → system context-adaptation">
          Each tier exposes the system to different talent constraints (D-II rosters, FCS recruitment, FBS Group-of-5 competitiveness). A coach who wins across these has been <em>forced</em> to adapt. The adaptation isn&rsquo;t theoretical; it&rsquo;s documented in the wins. By the time they reach the higher tier, the system has survival functions other coaches&rsquo; systems lack.
        </MechanismStep>

        <MechanismStep num={2} title="Voluntary following → locker-room validation">
          Players from prior stops vote with their feet. They know things a hiring committee can&rsquo;t access: how the system feels day-to-day, whether the coach develops players or burns through them, whether the wins came from talent extraction or talent dilution. <strong>Voluntary following is the inside-information check on the system claim.</strong>
        </MechanismStep>

        <MechanismStep num={3} title="Market mispricing → asymmetric edge availability">
          A consensus hire offers no edge. The hiring market is sophisticated enough that consensus candidates are priced correctly. An asymmetric bet only exists in spaces where the market has <em>missed something</em> — and the multi-level + voluntary-following signals are exactly the kind of information that&rsquo;s slow to propagate through the conventional hiring pipeline. The mispricing is the candidate&rsquo;s edge made visible.
        </MechanismStep>

        <MechanismStep num={4} title="System install → year-1 attack">
          With the system pre-built and the people-following to validate it, the new organization can install the system fast — within months, sometimes weeks. Mass roster action happens early; identity claims are made before wins; recruiting via the system rather than via charisma. Year-1 outcomes vary by talent baseline (Indiana 11-1 because they had a usable roster; Walsh 2-14 because they didn&rsquo;t), <strong>but the system is operating within months.</strong>
        </MechanismStep>

        <MechanismStep num={5} title="Year-2 doubling-down → championship-window unlock">
          This is the step that separates Cignetti from coaches who got conservative after their breakthrough. Pep won the treble in year 1 and refused to coast in year 2. Walsh kept investing in the WCO. Cignetti pulled Mendoza after going 11-1. <strong>The pattern&rsquo;s deepest finding: success doesn&rsquo;t make these leaders conservative. They escalate.</strong> The asymmetric bet compounds when the leader keeps aggression up after early wins.
        </MechanismStep>

        <p className="finding-prose">
          The Texas Tech failure inverts each step. Single-level wins → no documented context-adaptation. Negligible voluntary following → no inside-information validation. Market-rate hire → no asymmetric edge. System failed to install at higher tier → no year-1 attack. No year-2 doubling-down because there was no year-1 success to double down from. <strong>The chain broke at the first link and never recovered.</strong>
        </p>

        <p className="finding-prose">
          The mechanism&rsquo;s <em>causal-chain</em> status (rather than just <em>correlation observed</em>) comes from this inversion: the failure case fails <em>exactly where the success cases succeed</em>. That&rsquo;s not a correlation pattern; that&rsquo;s a mechanism with a falsifier.
        </p>
      </FindingSection>

      {/* ─── § How we could be wrong ─── */}
      <FindingSection eyebrow="§ How we could be wrong" title="The discipline requires honest exposure of where this could break.">
        <FindingSubhead>Sample size</FindingSubhead>
        <p className="finding-prose">
          N=4. Three successes + one failure across 1979-2025. The pattern is clear within the corpus; whether it generalizes is partially the open question this finding poses. <strong>A pre-registered prediction:</strong> at least one currently-active asymmetric-underdog hire we&rsquo;d identify as fitting all three signals will produce a championship-window outcome within 5 years; and at least one we&rsquo;d identify as missing one or more signals will fail to do so. To be calibrated as resolution dates arrive.
        </p>

        <FindingSubhead>Selection bias</FindingSubhead>
        <p className="finding-prose">
          We curated the four cases. Indiana, Walsh, and Pep are documented success stories that already populate strategic-decision discourse. Texas Tech is documented as the failed analog <em>in the source dossier</em>. The risk: many failed asymmetric hires went unwritten about; the corpus could be over-fitted to the cases that historiography already canonized. <strong>Mitigation:</strong> pre-register predictions on <em>currently-active</em> hires with the three-signal test before outcomes are known. Resolution dates expose any selection-bias contribution.
        </p>

        <FindingSubhead>Era effects</FindingSubhead>
        <p className="finding-prose">
          The Cignetti case lives in the NIL/transfer-portal era of NCAAF, which enables a recruiting pattern that pre-2018 college football couldn&rsquo;t sustain. Walsh&rsquo;s case is 1979 NFL. Pep&rsquo;s is mid-2000s La Liga with the Galácticos era&rsquo;s economics. The mechanism&rsquo;s chain assumes the <em>recruiting-via-system</em> step works — but the form that takes is era-dependent. Pre-NIL college, Walsh&rsquo;s NFL pre-free-agency, mid-2000s La Liga each had different talent-pipeline dynamics. <strong>The pattern may hold in spirit but require era-specific re-operationalization.</strong>
        </p>

        <FindingSubhead>Survivor bias on the failure analog side</FindingSubhead>
        <p className="finding-prose">
          Texas Tech is the documented failure. But many failed unconventional hires never enter strategic-decision discourse — the hire itself was unremarkable, the failure was unremarkable, no one wrote about it. The inverse-of-success story may be richer than just Texas Tech, and the <em>real</em> failure mode distribution may include sub-modes the pattern hasn&rsquo;t surfaced.
        </p>

        <FindingSubhead>The Pep edge case</FindingSubhead>
        <p className="finding-prose">
          Pep&rsquo;s multi-level signal is the weakest in the corpus — one HC role at the B level prior to first-team promotion. We&rsquo;ve classified him as multi-level &lsquo;2&rsquo; (B-team HC + lifelong club player) but a stricter classification would make him multi-level &lsquo;1&rsquo;, putting him in Wells&rsquo;s territory. The pattern <em>survived</em> Pep&rsquo;s edge case in the data, but if the mechanism were strict-multi-level-at-HC-level, Pep would have failed. He didn&rsquo;t. <strong>Either the mechanism is more flexible than the strict reading suggests, or Pep is a one-of pattern-breaker that the corpus needs to absorb.</strong> Calibration on future similar-shape cases will tell.
        </p>

        <TornadoPlot
          title="Falsifier sensitivity — alternative classifications"
          baselineLabel="finding holds"
          dimensions={FALSIFIER_DIMS}
        />

        <FindingSubhead>What would change my mind</FindingSubhead>
        <p className="finding-prose">
          Three observations would force revision or retraction:
        </p>
        <ol className="finding-numbered-list">
          <li>A currently-active asymmetric-underdog hire with all three signals present that fails to produce a championship-window outcome within 5 years (pre-registered, tracked).</li>
          <li>A documented case where all three signals were absent but the asymmetric bet succeeded anyway. This would mean the three-signal test is incomplete — there&rsquo;s a fourth signal we haven&rsquo;t surfaced.</li>
          <li>Cross-domain replication failure: the pattern surfaces in NCAAF + NFL + La Liga but doesn&rsquo;t survive translation to business/tech (e.g., Schultz&rsquo;s Starbucks return, Wojcicki at YouTube). If the mechanism is real and structural, it should transfer.</li>
        </ol>
      </FindingSection>

      {/* ─── § What this implies ─── */}
      <FindingSection eyebrow="§ What this implies" title="For a leader currently considering an unconventional hire.">
        <p className="finding-prose">
          <strong>Run the three-signal test before committing.</strong> Verify multi-level track record explicitly: not just &ldquo;they&rsquo;ve won places&rdquo; but won at <em>distinct tiers</em> with the system intact. Test for voluntary following via reference calls — would the assistants and players go where this candidate goes? Check market mispricing — has this candidate been available to peer organizations for years? If they&rsquo;ve been available and untaken, the mispricing is the edge.
        </p>

        <p className="finding-prose">
          <strong>If all three signals are present, the failure mode is your own intervention.</strong> The data says: protect the leader for 24 months minimum. Don&rsquo;t second-guess at month 9. Don&rsquo;t yank the system after a year-1 setback (Walsh&rsquo;s 2-14 would have been year-1 termination under conventional impatience; the 49ers waited and got the system). <strong>The intervention that breaks this pattern is the organization&rsquo;s intervention against the leader, not the leader&rsquo;s failure.</strong>
        </p>

        <p className="finding-prose">
          <strong>If even one signal is missing, do not proceed.</strong> Cosplay is documented. The Texas Tech playbook is what happens. The unconventional surface without the underlying signals is worse than the conventional hire because it adds organizational disruption to the mediocre baseline. Either find a candidate with all three signals or take the conventional hire. Don&rsquo;t run the pattern half-way.
        </p>

        <p className="finding-prose">
          <strong>Plan the year-2 doubling-down before year-1 ends.</strong> This is the discipline. If year 1 produces a breakthrough, the temptation to coast is real and documented. The pattern&rsquo;s deepest finding is that <em>successful asymmetric leaders escalate, they don&rsquo;t conserve.</em> Lock the year-2 talent-acquisition plan in writing before the year-1 results arrive, when the pressure isn&rsquo;t on yet.
        </p>

        <FindingSubhead>Pre-registered forward predictions</FindingSubhead>
        <ol className="finding-numbered-list">
          <li>
            <strong>Indiana 2026 season (Cignetti year 3):</strong> the pattern predicts Indiana will face the year-3 spotlight test and will not collapse — they&rsquo;ll produce another CFP-berth-tier season (10+ wins, top-15 finish), though probably not another undefeated title run. <em>Resolution: 2026 season end, January 2027. Falsifier: Cignetti record below .500 or Indiana fired.</em>
          </li>
          <li>
            <strong>Cross-domain test — Schultz at Starbucks (2008 return):</strong> the pattern predicts that a retrospective application of the three-signal test to Schultz&rsquo;s 2008 return will show all three signals were present. And the outcome should track the success cases. <em>Resolution: retrospective; immediate.</em>
          </li>
          <li>
            <strong>Anti-prediction — for any current asymmetric-underdog hire we identify with only one or two signals:</strong> the pattern predicts that hire will fail to produce championship-window outcomes within 5 years and the leader will be replaced or the organization will revert to baseline. <em>Resolution: case-specific.</em>
          </li>
        </ol>

        <PredictionsBlock
          title="Pre-registered forward predictions (2026-05-07)"
          predictions={PREDICTIONS}
        />
      </FindingSection>

      {/* ─── § What we're watching next ─── */}
      <FindingSection eyebrow="§ What we're watching next" title="Three open questions worth chasing.">
        <p className="finding-prose">
          <strong>1. Cross-domain transfer.</strong> Does the pattern hold in business/tech? The dossier names Schultz and Wojcicki as wild-card analogs. A natural next finding: extend the corpus to include 2-3 business cases and test whether the three-signal test discriminates winners from losers in non-sports contexts. If the pattern holds, mechanism is structural. If it fails to generalize, the pattern is sports-specific.
        </p>
        <p className="finding-prose">
          <strong>2. The Pep edge case clarification.</strong> Was Pep a multi-level success or a multi-level edge case where the other two signals carried the bet? Resolving this requires either: (a) finding additional analogous cases where multi-level is weak but mispricing + system-install are extreme, and observing whether the pattern holds; or (b) accepting that the mechanism has a more flexible multi-level dependency than strict-reading would suggest.
        </p>
        <p className="finding-prose">
          <strong>3. The post-championship trajectory.</strong> Indiana 2026 (year 3) is the live test of the pattern&rsquo;s <em>durability</em> claim. <strong>What does year-3 resilience look like in operational terms?</strong> A follow-up finding could document the year-3 dynamics across all four cases (Walsh 1981, Pep 2010-11, Cignetti 2026 — all year-3) and surface the specific operational practices that distinguish year-3 system-survival from year-3 system-collapse.
        </p>
      </FindingSection>

      {/* ─── Footer ledger ─── */}
      <footer className="finding-footer">
        <div className="finding-footer-eyebrow">Footer ledger</div>

        <dl className="finding-meta">
          <dt>Methodology Version</dt>
          <dd>Lab V2.0 (locked 2026-05-07)</dd>

          <dt>Form</dt>
          <dd>Form 4 — Pattern across cases</dd>

          <dt>Reflexivity</dt>
          <dd>LOW · <code>mode: full_prescriptive</code></dd>

          <dt>Sample size</dt>
          <dd>N=4 (3 successes + 1 failure analog)</dd>

          <dt>Epistemic status</dt>
          <dd>Mechanism documented · upgrade path to causal-chain traceable contingent on pre-registered prediction resolution</dd>

          <dt>Pre-registered</dt>
          <dd>2026-05-07. Predictions resolve from 2027-01.</dd>

          <dt>Sources</dt>
          <dd>
            Strategic Playbook dossier <code>indiana-2024-asymmetric</code> · NCAA football season records 2019-2025 · NFL records 1978-1989 · La Liga + UEFA Champions League records 2006-2012.
          </dd>

          <dt>Falsifier</dt>
          <dd>
            A documented case where all three signals are present and the asymmetric bet fails within 5 years; OR a case where all three are absent and the asymmetric bet succeeds; OR cross-domain replication failure.
          </dd>

          <dt>Citation handle</dt>
          <dd><code>lab:finding/strategic/2026/asymmetric-underdog-inversion/v1</code></dd>
        </dl>

        <div className="finding-citation">
          <strong>Cite as:</strong> Mai, T. (2026). <em>The Asymmetric Underdog Pattern: Three Signals That Separate Real Bets from Cosplay.</em> Lab Finding <code>lab:finding/strategic/2026/asymmetric-underdog-inversion/v1</code>. Methodology v2.0. Pre-registered 2026-05-07.
        </div>

        <div className="finding-engage">
          <div className="finding-footer-eyebrow">How to engage</div>
          <ul className="finding-bullets">
            <li><strong>Challenge:</strong> propose a counter-case (success without all three signals, or failure with all three).</li>
            <li><strong>Cite:</strong> the citation handle is stable across versions; v1 is this draft.</li>
            <li><strong>Fork:</strong> take the methodology + pre-state schema and run your own asymmetric-underdog evaluation on a candidate you&rsquo;re considering.</li>
          </ul>
        </div>

        <div className="finding-version">
          <div className="finding-footer-eyebrow">Update history</div>
          <p>v1 — 2026-05-07 — initial authoring (Tim Mai + Claude). Form 4 template applied. Pre-registered. Migrated from static page to dynamic route in Phase F1b.</p>
        </div>
      </footer>
    </article>
  );
}

// ─── Form 4 specific components ──────────────────────────────────────

function CaseCard({ num, title, outcome, year, children }) {
  const tone = outcome === 'success' ? 'ok' : outcome === 'failure' ? 'bad' : 'neutral';
  return (
    <div className={`finding-case-card finding-case-${tone}`}>
      <div className="finding-case-head">
        <span className="finding-case-num">Case {num}</span>
        <FindingPill tone={tone === 'ok' ? 'success' : tone === 'bad' ? 'failure' : 'neutral'}>{outcome}</FindingPill>
        <span className="finding-case-year">{year}</span>
      </div>
      <h3 className="finding-case-title">{title}</h3>
      {children}
    </div>
  );
}

function SignalCheck({ multilevel, voluntary, pricing }) {
  return (
    <div className="finding-signal-check">
      <div className="finding-signal-eyebrow">Three-signal read</div>
      <SignalRow label="Multi-level track record" {...multilevel} />
      <SignalRow label="Voluntary following" {...voluntary} />
      <SignalRow label="Market mispricing" {...pricing} />
    </div>
  );
}

function SignalRow({ label, value, detail, failed }) {
  return (
    <div className={failed ? 'finding-signal-row finding-signal-failed' : 'finding-signal-row'}>
      <div className="finding-signal-row-label">{label}</div>
      <div className="finding-signal-row-value">{value}</div>
      <div className="finding-signal-row-detail">{detail}</div>
    </div>
  );
}

function MechanismStep({ num, title, children }) {
  return (
    <div className="finding-mechanism-step">
      <div className="finding-mechanism-num">{num}</div>
      <div className="finding-mechanism-body">
        <div className="finding-mechanism-title">{title}</div>
        <div className="finding-mechanism-prose">{children}</div>
      </div>
    </div>
  );
}
